/**
 * useCamera — VehicleCameraModal preset
 *
 * Pre-configured CameraModal for vehicle inspection photos with vehicle
 * silhouette guides and orientation locking. No ML edge detection or
 * auto-capture — vehicle photos are manual capture only.
 *
 * Sensible defaults (all overridable):
 *   - guide mode: 'vehicle' (vehicle silhouette overlay)
 *   - autoCapture: false
 *   - autoZoom: false
 *   - reviewCapture: false
 *   - cropToGuide: false
 *
 * Usage:
 * ```tsx
 * const camera = useCamera({ onCapture: (b64) => ... });
 * return (
 *   <VehicleCameraModal
 *     camera={camera}
 *     title="Front Photo"
 *     vehicleProfile="front"
 *     preferredOrientation="landscape"
 *   />
 * );
 * ```
 */

import React from 'react';
import { CameraModal } from './CameraModal';
import type { UseCameraReturn } from './types';

export interface VehicleCameraModalProps {
  /** useCamera hook return value */
  camera: UseCameraReturn;
  /** Title shown in header */
  title: string;
  /** Subtitle shown in the guide overlay */
  subtitle?: string;
  /** Vehicle silhouette profile (default: 'front').
   *  'right_side' renders the side outline mirrored so the left and right
   *  walk-around photos face opposite directions. */
  vehicleProfile?: 'front' | 'rear' | 'side' | 'left_side' | 'right_side' | 'interior' | 'dashboard';
  /** Preferred device orientation for this capture */
  preferredOrientation?: 'portrait' | 'landscape';
  /** Called after successful capture */
  onAfterCapture?: () => void;
  /** Custom capture button */
  captureButton?: React.ReactNode;
}

export function VehicleCameraModal({
  camera,
  title,
  subtitle = 'Center the vehicle in the frame',
  vehicleProfile = 'front',
  preferredOrientation,
  onAfterCapture,
  captureButton,
}: VehicleCameraModalProps) {
  return (
    <CameraModal
      camera={camera}
      title={title}
      guide={{ mode: 'vehicle', title, subtitle }}
      vehicleProfile={vehicleProfile}
      preferredOrientation={preferredOrientation}
      autoCapture={false}
      autoZoom={false}
      reviewCapture={false}
      cropToGuide={false}
      onAfterCapture={onAfterCapture}
      captureButton={captureButton}
    />
  );
}

export default VehicleCameraModal;
